import React, { ReactNode, useEffect, useState } from "react";
import { UseFormRegisterReturn, useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { PASSWORD_POLICY_MESSAGE, UiError } from "../../../auth/native/nativeAuthErrors";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

interface CommonFormProps {
  busy: boolean;
  error: UiError | null;
  info: string | null;
  /** Focus the first field on mount. Off for the admin preview. */
  autoFocus?: boolean;
}

export function AuthMessages({ error, info }: { error: UiError | null; info: string | null }) {
  return (
    <>
      {error?.message && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error.message}
          {error.reference && (
            <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
              Reference: {error.reference}
            </Typography>
          )}
        </Alert>
      )}
      {info && (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}
    </>
  );
}

function SubmitButton({ busy, busyLabel, children }: { busy: boolean; busyLabel: string; children: ReactNode }) {
  return (
    <Button type="submit" variant="contained" size="large" fullWidth disabled={busy} sx={{ py: 1.25 }}>
      {busy ? (
        <>
          <CircularProgress size={20} color="inherit" aria-hidden="true" sx={{ mr: 1 }} />
          {busyLabel}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

function TextLinkButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <Link component="button" type="button" variant="body2" onClick={onClick}>
      {children}
    </Link>
  );
}

interface PasswordInputProps {
  registration: UseFormRegisterReturn;
  label: string;
  autoComplete: "current-password" | "new-password";
  errorMessage?: string;
  invalid?: boolean;
  helperText?: string;
  autoFocus?: boolean;
}

function PasswordInput({ registration, label, autoComplete, errorMessage, invalid, helperText, autoFocus }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const { ref, ...field } = registration;

  return (
    <TextField
      {...field}
      inputRef={ref}
      label={label}
      type={visible ? "text" : "password"}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      required
      fullWidth
      error={!!errorMessage || !!invalid}
      helperText={errorMessage ?? helperText}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={() => setVisible((current) => !current)}
                aria-label={visible ? "Hide password" : "Show password"}
                aria-pressed={visible}
              >
                {visible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

function EmailInput({
  registration,
  errorMessage,
  invalid,
  autoComplete,
  autoFocus,
}: {
  registration: UseFormRegisterReturn;
  errorMessage?: string;
  invalid?: boolean;
  autoComplete: "username" | "email";
  autoFocus?: boolean;
}) {
  const { ref, ...field } = registration;
  return (
    <TextField
      {...field}
      inputRef={ref}
      label="Email"
      type="email"
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      required
      fullWidth
      error={!!errorMessage || !!invalid}
      helperText={errorMessage}
      slotProps={{ htmlInput: { inputMode: "email", autoCapitalize: "none", spellCheck: false } }}
    />
  );
}

const emailRules = {
  required: "Enter your email address.",
  pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address." },
};

interface SignInFormProps extends CommonFormProps {
  onSubmit: (email: string, password: string) => void;
  onForgotPassword: () => void;
  onCreateAccount: () => void;
}

export function SignInForm({ busy, error, info, autoFocus = true, onSubmit, onForgotPassword, onCreateAccount }: SignInFormProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<{ email: string; password: string }>({
    defaultValues: { email: "", password: "" },
  });

  return (
    <Box component="form" noValidate aria-busy={busy} onSubmit={handleSubmit((values) => onSubmit(values.email.trim(), values.password))}>
      <AuthMessages error={error} info={info} />
      <Stack spacing={2}>
        <EmailInput
          registration={register("email", emailRules)}
          errorMessage={errors.email?.message}
          invalid={error?.field === "email"}
          autoComplete="username"
          autoFocus={autoFocus}
        />
        <PasswordInput
          registration={register("password", { required: "Enter your password." })}
          label="Password"
          autoComplete="current-password"
          errorMessage={errors.password?.message}
          invalid={error?.field === "password"}
        />
        <SubmitButton busy={busy} busyLabel="Signing in…">
          Sign in
        </SubmitButton>
      </Stack>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <TextLinkButton onClick={onForgotPassword}>Forgot password?</TextLinkButton>
        <TextLinkButton onClick={onCreateAccount}>Create an account</TextLinkButton>
      </Box>
    </Box>
  );
}

interface SignUpFormProps extends CommonFormProps {
  /** Linked from the consent checkbox; the affiliate's own terms when it has them. */
  termsUrl?: string | null;
  /** False when the tenant records no consent attribute, so there is nothing to ask for. */
  collectTerms?: boolean;
  onSubmit: (
    email: string,
    password: string,
    profile: { displayName: string; agreedToTerms: boolean }
  ) => void;
  onSignIn: () => void;
}

export function SignUpForm({
  busy,
  error,
  info,
  autoFocus = true,
  termsUrl,
  collectTerms = true,
  onSubmit,
  onSignIn,
}: SignUpFormProps) {
  const { register, handleSubmit, getValues, formState: { errors } } = useForm<{
    displayName: string;
    email: string;
    password: string;
    confirmPassword: string;
    agreedToTerms: boolean;
  }>({
    defaultValues: { displayName: "", email: "", password: "", confirmPassword: "", agreedToTerms: false },
  });

  const nameField = register("displayName", {
    required: "Enter your name.",
    maxLength: { value: 64, message: "Use 64 characters or fewer." },
  });
  const { ref: nameRef, ...name } = nameField;

  return (
    <Box
      component="form"
      noValidate
      aria-busy={busy}
      onSubmit={handleSubmit((values) =>
        onSubmit(values.email.trim(), values.password, {
          displayName: values.displayName.trim(),
          agreedToTerms: !!values.agreedToTerms,
        })
      )}
    >
      <AuthMessages error={error} info={info} />
      <Stack spacing={2}>
        <TextField
          {...name}
          inputRef={nameRef}
          label="Full name"
          autoComplete="name"
          autoFocus={autoFocus}
          required
          fullWidth
          error={!!errors.displayName}
          helperText={errors.displayName?.message}
        />
        <EmailInput
          registration={register("email", emailRules)}
          errorMessage={errors.email?.message}
          invalid={error?.field === "email"}
          autoComplete="email"
        />
        <PasswordInput
          registration={register("password", {
            required: "Create a password.",
            minLength: { value: MIN_PASSWORD_LENGTH, message: PASSWORD_POLICY_MESSAGE },
          })}
          label="Password"
          autoComplete="new-password"
          errorMessage={errors.password?.message}
          invalid={error?.field === "password"}
          helperText={PASSWORD_POLICY_MESSAGE}
        />
        <PasswordInput
          registration={register("confirmPassword", {
            required: "Re-enter your password.",
            validate: (value) => value === getValues("password") || "Passwords don't match.",
          })}
          label="Confirm password"
          autoComplete="new-password"
          errorMessage={errors.confirmPassword?.message}
        />
        {collectTerms && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  {...register("agreedToTerms", { required: "Accept the terms to continue." })}
                  slotProps={{ input: { "aria-describedby": "terms-error" } }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  {termsUrl ? (
                    <Link href={termsUrl} target="_blank" rel="noopener noreferrer">
                      terms and conditions
                    </Link>
                  ) : (
                    "terms and conditions"
                  )}
                </Typography>
              }
            />
            {errors.agreedToTerms && (
              <FormHelperText id="terms-error" error>
                {errors.agreedToTerms.message}
              </FormHelperText>
            )}
          </Box>
        )}
        <SubmitButton busy={busy} busyLabel="Creating account…">
          Create account
        </SubmitButton>
      </Stack>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Already have an account? <TextLinkButton onClick={onSignIn}>Sign in</TextLinkButton>
      </Typography>
    </Box>
  );
}

function useSecondsUntil(timestamp: number): number {
  const [seconds, setSeconds] = useState(() => Math.max(0, Math.ceil((timestamp - Date.now()) / 1000)));

  useEffect(() => {
    const remaining = () => Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
    setSeconds(remaining());
    if (timestamp <= Date.now()) {
      return;
    }
    const interval = setInterval(() => {
      const next = remaining();
      setSeconds(next);
      if (next === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return seconds;
}

interface CodeFormProps extends CommonFormProps {
  email: string;
  codeLength: number;
  resendAvailableAt: number;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  backLabel: string;
}

export function CodeForm({ busy, error, info, email, codeLength, resendAvailableAt, onSubmit, onResend, onBack, backLabel, autoFocus = true }: CodeFormProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<{ code: string }>({ defaultValues: { code: "" } });
  const secondsUntilResend = useSecondsUntil(resendAvailableAt);
  const exactLength = codeLength > 0;
  const { ref, ...field } = register("code", {
    required: "Enter the code from your email.",
    setValueAs: (value: string) => value.replace(/\s/g, ""),
    pattern: exactLength
      ? { value: new RegExp(`^\\d{${codeLength}}$`), message: `Enter the ${codeLength}-digit code.` }
      : { value: /^\d{4,10}$/, message: "Enter the code from your email." },
  });

  return (
    <Box component="form" noValidate aria-busy={busy} onSubmit={handleSubmit((values) => onSubmit(values.code))}>
      <AuthMessages error={error} info={info} />
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Enter the {exactLength ? `${codeLength}-digit ` : ""}code we sent to <strong>{email}</strong>.
      </Typography>
      <Stack spacing={2}>
        <TextField
          {...field}
          inputRef={ref}
          label="Verification code"
          autoComplete="one-time-code"
          autoFocus={autoFocus}
          required
          fullWidth
          error={!!errors.code || error?.field === "code"}
          helperText={errors.code?.message}
          slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*", maxLength: exactLength ? codeLength : 10 } }}
        />
        <SubmitButton busy={busy} busyLabel="Verifying…">
          Verify
        </SubmitButton>
        <Button type="button" variant="text" onClick={onResend} disabled={busy || secondsUntilResend > 0}>
          {secondsUntilResend > 0 ? `Resend code in ${secondsUntilResend}s` : "Resend code"}
        </Button>
      </Stack>
      <Box sx={{ mt: 1, textAlign: "center" }}>
        <TextLinkButton onClick={onBack}>{backLabel}</TextLinkButton>
      </Box>
    </Box>
  );
}

interface NewPasswordFormProps extends CommonFormProps {
  submitLabel: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
  backLabel: string;
  /** "current-password" when the SDK asks for an existing password during sign-in. */
  mode?: "new" | "current";
}

export function PasswordForm({ busy, error, info, submitLabel, onSubmit, onBack, backLabel, mode = "new", autoFocus = true }: NewPasswordFormProps) {
  const { register, handleSubmit, getValues, formState: { errors } } = useForm<{ password: string; confirmPassword: string }>({
    defaultValues: { password: "", confirmPassword: "" },
  });
  const isNew = mode === "new";

  return (
    <Box component="form" noValidate aria-busy={busy} onSubmit={handleSubmit((values) => onSubmit(values.password))}>
      <AuthMessages error={error} info={info} />
      <Stack spacing={2}>
        <PasswordInput
          registration={register("password", {
            required: isNew ? "Create a password." : "Enter your password.",
            minLength: isNew ? { value: MIN_PASSWORD_LENGTH, message: PASSWORD_POLICY_MESSAGE } : undefined,
          })}
          label={isNew ? "New password" : "Password"}
          autoComplete={isNew ? "new-password" : "current-password"}
          errorMessage={errors.password?.message}
          invalid={error?.field === "password"}
          helperText={isNew ? PASSWORD_POLICY_MESSAGE : undefined}
          autoFocus={autoFocus}
        />
        {isNew && (
          <PasswordInput
            registration={register("confirmPassword", {
              required: "Re-enter your password.",
              validate: (value) => value === getValues("password") || "Passwords don't match.",
            })}
            label="Confirm new password"
            autoComplete="new-password"
            errorMessage={errors.confirmPassword?.message}
          />
        )}
        <SubmitButton busy={busy} busyLabel="Saving…">
          {submitLabel}
        </SubmitButton>
      </Stack>
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <TextLinkButton onClick={onBack}>{backLabel}</TextLinkButton>
      </Box>
    </Box>
  );
}

interface ResetPasswordStartFormProps extends CommonFormProps {
  onSubmit: (email: string) => void;
  onBack: () => void;
}

export function ResetPasswordStartForm({ busy, error, info, onSubmit, onBack, autoFocus = true }: ResetPasswordStartFormProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<{ email: string }>({ defaultValues: { email: "" } });

  return (
    <Box component="form" noValidate aria-busy={busy} onSubmit={handleSubmit((values) => onSubmit(values.email.trim()))}>
      <AuthMessages error={error} info={info} />
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Enter the email for your account and we'll send you a verification code.
      </Typography>
      <Stack spacing={2}>
        <EmailInput
          registration={register("email", emailRules)}
          errorMessage={errors.email?.message}
          invalid={error?.field === "email"}
          autoComplete="username"
          autoFocus={autoFocus}
        />
        <SubmitButton busy={busy} busyLabel="Sending code…">
          Send code
        </SubmitButton>
      </Stack>
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <TextLinkButton onClick={onBack}>Back to sign in</TextLinkButton>
      </Box>
    </Box>
  );
}
